import * as React from "react";
import { observer } from "mobx-react";
import { action } from "mobx";
import {
    ComboBox,
    type IComboBoxOption,
    type IComboBoxProps,
    type ISelectableOption,
} from "@fluentui/react";
import { Language } from "../../api";
import { LoadingState } from "../../utils/loading-state";
import { type UUID } from "../../utils/uuid";

export type FormUpdateDefaultLanguageProps = Omit<
    IComboBoxProps,
    "options" | "selectedKey" | "onChange"
>;

enum UpdateDefaultLanguageFeatures {
    CHANGE_DEFAULT_LANGUAGE = "change default language",
}

import { store } from "../../store";

@observer
export class FormUpdateDefaultLanguage extends React.Component<FormUpdateDefaultLanguageProps> {
    private readonly loadingFeatures = new LoadingState<UpdateDefaultLanguageFeatures>();

    private get options(): ISelectableOption<unknown>[] {
        return Object.values(Language).map((language) => ({
            key: language,
            text: store.i18n.t(`formUpdateDefaultLanguage.language.${language}`),
        }));
    }

    @action.bound private async updateDefaultLanguage(
        publicSettingId: UUID,
        options: IComboBoxOption | undefined,
    ): Promise<void> {
        const language = options?.key as Language;
        await this.loadingFeatures.wrap(
            UpdateDefaultLanguageFeatures.CHANGE_DEFAULT_LANGUAGE,
            store.repositoryPublicSettings.update(publicSettingId, {
                defaultLanguage: language,
            }),
        );
    }

    private renderOption(option: ISelectableOption<unknown> | undefined): JSX.Element {
        if (!option) {
            return <></>;
        }
        return (
            <>
                <span style={{ marginRight: "1em" }} />
                {option.text}
            </>
        );
    }

    public render(): JSX.Element {
        return (
            <ComboBox
                {...this.props}
                disabled={
                    this.loadingFeatures.isLoading(
                        UpdateDefaultLanguageFeatures.CHANGE_DEFAULT_LANGUAGE,
                    ) || this.props.disabled
                }
                label={store.i18n.t("formUpdateDefaultLanguage.label")}
                options={this.options}
                selectedKey={
                    this.loadingFeatures.isLoading(
                        UpdateDefaultLanguageFeatures.CHANGE_DEFAULT_LANGUAGE,
                    )
                        ? undefined
                        : store.servicePublicSettings.publicSetting?.defaultLanguage
                }
                onRenderOption={(option) => this.renderOption(option)}
                onChange={(_, option) => {
                    if (store.servicePublicSettings.publicSetting) {
                        this.updateDefaultLanguage(
                            store.servicePublicSettings.publicSetting.id,
                            option,
                        );
                    }
                }}
            />
        );
    }
}
